import { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useContent } from '@/hooks/useContent';
import { interpolateContent } from '@/lib/content';

import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme, alpha } from '@mui/material/styles';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import deepHealthLogo from '@/assets/deephealth-logo.svg';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { QuickTutorialModal } from '@/components/common/QuickTutorialModal';
import { authUserMetaAtom } from '@/store/authAtoms';
import { remoteAccessContextAtom } from '@/store/remoteAccessContextAtom';
import { tutorialOpenAtom } from '@/store/tutorialAtom';
import { useLegalLinks } from '@/hooks/useLegalLinks';
import { LegalFooterLinks } from '@/components/common/LegalFooterLinks';
import { patientProfileAtom } from '@/store/patientProfileAtom';
import { authService } from '@/services/authService';
import { patientService } from '@/services/patientService';
import { ToastContainer, useToast } from '@/components/common/Toast';
import { AriaFab } from '@/components/common/AriaFab';
import { ParagraphCodeModeBadge } from '@/components/common/ParagraphCodeModeBadge';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';
import NavAccountLogin from '@/components/common/NavAccountLogin';
import NavCreateAccount from '@/components/common/NavCreateAccount';
import { navAccountLoginActiveAtom } from '@/store/navAccountLoginAtom';
import { IdleTimeoutDialog, AbsoluteTimeoutDialog } from '@/components/common/SessionTimeoutModal';
import { useTranslation } from 'react-i18next';
import { useSessionCaps } from '@/hooks/useSessionCaps';
import { useConfig, isYes } from '@/hooks/useConfig';
import { useLiteMode } from '@/hooks/useLiteMode';
import { useLongPress } from '@/hooks/useLongPress';
import { useScreenSession } from '@/hooks/useScreenSession';
import { VersionInfoPopover } from '@/components/common/VersionInfoPopover';
import Alert from '@mui/material/Alert';
import { viewingAsAtom, homeUserMetaAtom, delegateAccessEndReasonAtom, delegateAccessFailureNoticeAtom } from '@/store/authAtoms';
import { medicalRecordAccessService, type AccessiblePatient } from '@/services/medicalRecordAccessService';
import { PortalNotifications, usePortalNavBadges } from '@/components/common/PortalNotifications';

const SIDEBAR_EXPANDED_W = 256;
const SIDEBAR_COLLAPSED_W = 64;

/* ── Authorized-access delegate context banner ── */
const viewedPatientClinicalQueryKeys = [
  'patientAttachments',
  'patientAttachmentPage',
  'pacsStudy',
  'dicomStatus',
  'dicomMediaActive',
  'dicomMediaHistory',
  'gfeDocument',
  'exam-activity-log',
] as const;

/** Drop clinical caches that are not keyed by the acting user before the next identity's reads. */
function clearViewedPatientClinicalCache(queryClient: QueryClient) {
  for (const key of viewedPatientClinicalQueryKeys) {
    queryClient.removeQueries({ queryKey: [key] });
  }
  void queryClient.invalidateQueries({ queryKey: ['exams'] });
  void queryClient.invalidateQueries({ queryKey: ['appointments'] });
}

function AuthorizedAccessBanner() {
  const { getContent, getContentString } = useContent();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [viewingAs, setViewingAs] = useAtom(viewingAsAtom);
  const [user, setMeta] = useAtom(authUserMetaAtom);
  const [homeUserMeta, setHomeUserMeta] = useAtom(homeUserMetaAtom);
  const setProfile = useSetAtom(patientProfileAtom);
  const [switching, setSwitching] = useState(false);

  const accessibleKey = ['authorizedAccessiblePatients', user?.id] as const;

  // Only fetch "which patients can I view" with the delegate's OWN identity — while
  // already viewing-as, authUserMetaAtom holds the target patient's context, which
  // would answer this question for the wrong person.
  const accessible = useQuery({
    queryKey: accessibleKey,
    queryFn: () => medicalRecordAccessService.getAccessiblePatients(),
    enabled: !!user?.id && !viewingAs,
  });

  const switchTo = async (patientKey: number, name: string) => {
    setSwitching(true);
    try {
      const result = await medicalRecordAccessService.activatePatientContext(patientKey, name);
      setHomeUserMeta(user);
      setMeta({ id: String(result.patientKey), fullName: result.userName, roles: user?.roles ?? ['patient'] });
      setViewingAs({ patientKey: result.patientKey, name: result.name || name });
      setProfile(null);
      clearViewedPatientClinicalCache(queryClient);
    } catch {
      // Most likely: the grant was revoked between page load and this click — exactly
      // the race the backend re-validates against server-side. Drop it from the banner
      // immediately (don't make the user wait for a full refetch to stop seeing a dead
      // entry), then reconcile with the server in the background.
      toast(getContentString('PPDelegateSwitchError'), 'error');
      queryClient.setQueryData<AccessiblePatient[]>(accessibleKey, (old) =>
        (old ?? []).filter((p) => p.patientKey !== patientKey),
      );
      void queryClient.invalidateQueries({ queryKey: accessibleKey });
    } finally {
      setSwitching(false);
    }
  };

  const switchBack = () => {
    void medicalRecordAccessService.switchBack().then(() => {
      localStorage.removeItem('delegate_token');
      if (homeUserMeta) setMeta(homeUserMeta);
      setHomeUserMeta(null);
      setViewingAs(null);
      setProfile(null);
      clearViewedPatientClinicalCache(queryClient);
    }).catch(() => {
      toast(getContentString('PPDelegateSwitchError'), 'error');
    });
  };

  if (viewingAs) {
    return (
      <Alert
        severity="warning"
        variant="filled"
        sx={{ borderRadius: 0, alignItems: 'center' }}
        action={
          <Button color="inherit" size="small" variant="outlined" onClick={switchBack}>
            {getContent('PPDelegateSwitchBack')}
          </Button>
        }
      >
        {getInterpolatedText(getContentString('PPDelegateViewingAs'), { name: viewingAs.name })}
      </Alert>
    );
  }

  if (!accessible.data || accessible.data.length === 0) return null;

  return (
    <Alert severity="info" variant="filled" sx={{ borderRadius: 0, alignItems: 'center' }}>
      <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
          {getInterpolatedText(getContentString('PPDelegateAccessibleCount'), { count: accessible.data.length })}
        </Typography>
        {accessible.data.map((p) => (
          <Button
            key={p.patientKey}
            color="inherit"
            variant="outlined"
            size="small"
            disabled={switching}
            onClick={() => void switchTo(p.patientKey, `${p.firstName} ${p.lastName}`.trim())}
          >
            {p.firstName} {p.lastName}
          </Button>
        ))}
      </Stack>
    </Alert>
  );
}

function getInterpolatedText(template: string, tokens: Record<string, string | number>): string {
  return Object.entries(tokens).reduce(
    (text, [key, value]) => text.replaceAll(`{{${key}}}`, String(value)),
    template,
  );
}

/* ── Logo / Wordmark ── */
function DHWordmark({ compact = false }: Readonly<{ compact?: boolean }>) {
  // Per-facility branding overrides (set via pp_configuration_override).
  // Falls back to DeepHealth defaults when nothing is configured.
  // All useConfig calls are unconditional — hooks must not be called inside ?? short-circuit chains.
  const portalLogoUrl      = useConfig('PortalLogo');
  const patientPortalName  = useConfig('PatientPortalName');
  const portalNameConfig   = useConfig('PortalName');
  const portalShortName    = useConfig('PortalShortName');
  const { getContent }     = useContent();

  const portalName  = patientPortalName  ?? portalNameConfig ?? 'Patient Portal';
  const portalShort = portalShortName    ?? portalNameConfig ?? getContent('PPPortalDefaultName');

  const titleRef = useRef<HTMLDivElement>(null);
  const [versionAnchor, setVersionAnchor] = useState<HTMLElement | null>(null);
  const longPressHandlers = useLongPress(() => {
    if (titleRef.current) setVersionAnchor(titleRef.current);
  });

  return (
    <>
      <VersionInfoPopover
        anchorEl={versionAnchor}
        onClose={() => setVersionAnchor(null)}
        showReset={false}
      />
    <Stack ref={titleRef} direction="row" spacing={compact ? 0 : 1.25} {...longPressHandlers} sx={{ alignItems: 'center', justifyContent: compact ? 'center' : 'flex-start', flexShrink: 0, cursor: 'default', userSelect: 'none' }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          // Use the theme primary gradient so the badge picks up the practice's
          // chosen palette automatically, instead of the hardcoded purple.
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          boxShadow: 2,
          overflow: 'hidden',
        }}
      >
        {portalLogoUrl ? (
          <Box component="img" src={portalLogoUrl} alt="" aria-hidden sx={{ width: 28, height: 28, objectFit: 'contain' }} />
        ) : (
          <svg viewBox="0 0 24 24" fill="white" width={18} height={18} aria-hidden="true" focusable="false">
            <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
          </svg>
        )}
      </Box>
      {!compact && (
        <Box sx={{ overflow: 'hidden', display: { xs: 'none', sm: 'block' } }}>
          {/* Figma Sidebar branding: single bold facility name next to logo */}
          <Typography
            variant="subtitle1"
            color="text.primary"
            noWrap
            sx={{ fontWeight: 900, fontSize: 18, lineHeight: '20px', letterSpacing: 0 }}
          >
            {portalName}
          </Typography>
          <Typography component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
            {portalShort}
          </Typography>
        </Box>
      )}
    </Stack>
    </>
  );
}

/* ── Main Layout ── */
export default function AppLayout() {
  const [user, setMeta] = useAtom(authUserMetaAtom);
  const remoteAccessCtx = useAtomValue(remoteAccessContextAtom);
  const isRemoteAccess = remoteAccessCtx !== null;
  const isNoAccountRemoteAccess = remoteAccessCtx?.isNoAccount === true;
  const [profile, setProfile] = useAtom(patientProfileAtom);
  const viewingAs = useAtomValue(viewingAsAtom);
  const delegateAccessFailureNotice = useAtomValue(delegateAccessFailureNoticeAtom);
  const delegateAccessEndReason = useAtomValue(delegateAccessEndReasonAtom);
  const lastDelegateAccessFailureNotice = useRef(0);
  const setViewingAs = useSetAtom(viewingAsAtom);
  const setHomeUserMeta = useSetAtom(homeUserMetaAtom);
  const location = useLocation();
  const { t } = useTranslation();
  const { getContent, getContentString } = useContent();
  const { toast } = useToast();
  const muiTheme = useTheme();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [mobileOpen, setMobileOpen]       = useState(false);
  const [tutorialOpen, setTutorialOpen]   = useAtom(tutorialOpenAtom);
  const { canAccess, isGuestSession, isICodeSession } = useSessionCaps();

  const [routeAnnouncement, setRouteAnnouncement] = useState('');
  const prevPathRef = useRef(location.pathname);

  const { links: legalLinks } = useLegalLinks();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const stored = localStorage.getItem('sidebar-collapsed');
    return stored === null ? true : stored === 'true';
  });
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const navAccountLoginActive = useAtomValue(navAccountLoginActiveAtom);

  // Per-facility feature gates — must be read before the inbox query so we can
  // skip a known-disabled round-trip. Stacked with canAccess() further below.
  //
  // AppConfig keys (pp_configuration_override → appsettings.json → default):
  const healthSummaryEnabled = isYes(useConfig('PPHealthSummaryEnabled'));
  const helpEnabled          = isYes(useConfig('PPGetHelpAccessEnabled'));
  const messagingEnabled     = isYes(useConfig('PPMessagesAccessEnabled'));
  const lite = useLiteMode();
  // (no SystemConfig keys read here today — add `useSystemConfig(...)` if needed)

  // Nav badges + notification panel share Dashboard worklist/inbox query keys (no duplicate fetches).
  const { newResults: newResultsBadge, upcoming: upcomingBadge, unreadMessages } = usePortalNavBadges();

  // NOTE: this shared patientProfileAtom is only loaded for the normal full-session patient.
  // While viewing-as, /Patient/* is intentionally outside the delegate-token controller
  // allowlist, so fetching the target patient's profile here would correctly 403 and add
  // noisy API errors. Account.tsx has its own accountIdentityAtom-scoped
  // profile query for the logged-in user's own identity.
  const { data: fetchedProfile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: () => patientService.getProfile(Number(user!.id)),
    // Restricted iCode sessions (e.g. GFE) don't need profile data and may not carry a
    // renewable access token — skip the fetch the same way guest sessions are skipped.
    enabled: !!user?.id && !viewingAs && !isGuestSession && !isICodeSession,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    if (fetchedProfile) setProfile(fetchedProfile);
  }, [fetchedProfile, setProfile]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem('sidebar-collapsed', String(next));
      return next;
    });
  }, []);

  const handleLogout = useCallback(() => {
    void authService.logout().catch(() => undefined);
    setMeta(null);
    setProfile(null);
    // Viewing metadata is persisted — without this, a delegate session's banner
    // would survive into the next login on the same browser.
    setViewingAs(null);
    setHomeUserMeta(null);
    localStorage.removeItem('delegate_token');
  }, [setMeta, setProfile, setViewingAs, setHomeUserMeta]);

  useEffect(() => {
    if (delegateAccessFailureNotice <= lastDelegateAccessFailureNotice.current) return;
    lastDelegateAccessFailureNotice.current = delegateAccessFailureNotice;
    const code = delegateAccessEndReason === 'revoked' ? 'PPDelegateAccessRevoked' : 'PPDelegateAccessExpired';
    toast(getContentString(code), 'error');
  }, [delegateAccessEndReason, delegateAccessFailureNotice, getContentString, toast]);

  const { phase, countdown, extend, logout: sessionLogout } = useScreenSession();

  const shareInNav =
    !isGuestSession
    && !isICodeSession
    && !isRemoteAccess
    && lite.isSectionVisible('exams')
    && lite.isActionVisible('exams', 'share');

  const tabs = [
    { to: '/dashboard',      contentKey: 'PPNavDashboard',     fallback: 'nav.dashboard',     icon: <DashboardOutlinedIcon />, badge: 0, show: lite.isSectionVisible('dashboard') },
    { to: '/exams',          contentKey: 'PPNavExams',         fallback: 'nav.exams',         icon: <AssignmentOutlinedIcon />, badge: newResultsBadge, show: lite.isSectionVisible('exams') },
    { to: '/appointments',   contentKey: 'PPNavAppointments',  fallback: 'nav.appointments',  icon: <CalendarMonthOutlinedIcon />, badge: upcomingBadge, show: lite.isSectionVisible('appointments') },
    { to: '/health-summary', contentKey: 'PPNavHealthSummary', fallback: 'nav.healthSummary', icon: <FavoriteBorderIcon />,    badge: 0, show: healthSummaryEnabled && lite.isSectionVisible('healthSummary') },
    { to: '/messages',       contentKey: 'PPNavMessages',      fallback: 'nav.messages',      icon: <ChatBubbleOutlineIcon />, badge: unreadMessages, show: messagingEnabled && !isNoAccountRemoteAccess && lite.isSectionVisible('messages') },
    { to: '/share',          contentKey: 'PPNavShare',         fallback: 'nav.share',         icon: <ShareOutlinedIcon />,     badge: 0, show: shareInNav },
    { to: '/help',           contentKey: 'PPNavHelp',          fallback: 'nav.help',          icon: <HelpOutlineIcon />,       badge: 0, show: helpEnabled && lite.isSectionVisible('help') },
  ].filter((tab) => tab.show && canAccess(tab.to));

  function resolvePageName(pathname: string, tabDefs: { to: string; contentKey: string; fallback: string }[]): string {
    const matched = tabDefs.find(tab => pathname.startsWith(tab.to));
    return matched ? (getContentString(matched.contentKey) || t(matched.fallback)) : 'Patient Portal';
  }

  useEffect(() => {
    if (location.pathname === prevPathRef.current) return;
    prevPathRef.current = location.pathname;
    const allTabDefs = [...tabs, { to: '/account', contentKey: 'PPNavAccount', fallback: 'nav.account' }];
    const pageName = resolvePageName(location.pathname, allTabDefs);
    document.title = `${pageName} — Patient Portal`;
    setRouteAnnouncement(interpolateContent(getContentString('PPNavNavigatedTo') || t('nav.navigatedTo'), { page: pageName }));
  });

  // Fixed 4-item bottom bar: Dashboard / Results / Appointments / Messages.
  // Keep the active route's tab visible. Account, Help, Health Summary, Share stay in the sidebar/hamburger.
  const mobileBottomTabs = [
    { to: '/dashboard',      contentKey: 'PPNavDashboard',     fallback: 'nav.dashboard',     icon: <DashboardOutlinedIcon />,     badge: 0, show: lite.isSectionVisible('dashboard') },
    { to: '/exams',          contentKey: 'PPNavExams',         fallback: 'nav.exams',         icon: <AssignmentOutlinedIcon />,    badge: newResultsBadge, show: lite.isSectionVisible('exams') },
    { to: '/appointments',   contentKey: 'PPNavAppointments',  fallback: 'nav.appointments',  icon: <CalendarMonthOutlinedIcon />, badge: upcomingBadge, show: lite.isSectionVisible('appointments') },
    { to: '/messages',       contentKey: 'PPNavMessages',      fallback: 'nav.messages',      icon: <ChatBubbleOutlineIcon />,     badge: unreadMessages, show: messagingEnabled && !isNoAccountRemoteAccess && lite.isSectionVisible('messages') },
  ].filter((tab) => tab.show && canAccess(tab.to));

  const visibleBottomTabs = mobileBottomTabs;

  // Resizable sidebar
  const [sidebarDragWidth, setSidebarDragWidth] = useState<number | null>(null);
  const sidebarDragging = useRef(false);

  const onSidebarDragStart = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    sidebarDragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (ev: PointerEvent) => {
      if (!sidebarDragging.current) return;
      const newW = Math.max(SIDEBAR_COLLAPSED_W, Math.min(ev.clientX, 400));
      setSidebarDragWidth(newW);
    };
    const onUp = () => {
      sidebarDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);

      setSidebarDragWidth((w) => {
        if (w !== null && w <= SIDEBAR_COLLAPSED_W + 20) {
          if (!collapsed) toggleCollapsed();
          return null;
        }
        if (w !== null && collapsed) toggleCollapsed();
        return w;
      });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [collapsed, toggleCollapsed]);

  // Hover-expand: when collapsed, hovering temporarily expands the rail without
  // flipping the persisted preference. Dragging takes precedence (sidebarDragWidth).
  // Keep the rail expanded while the nav login form has focus — otherwise mouse-leave
  // re-collapses it and unmounts NavAccountLogin, wiping out whatever was typed.
  const visuallyCollapsed = collapsed && !sidebarHovered && !navAccountLoginActive;
  const sidebarWidth = sidebarDragWidth ?? (visuallyCollapsed ? SIDEBAR_COLLAPSED_W : SIDEBAR_EXPANDED_W);

  const bottomNavValue = visibleBottomTabs.findIndex(tab => location.pathname.startsWith(tab.to));

  // Top header actions — notification bell for full sessions (not guest/iCode/no-account remote).
  const showHeaderBell = !isGuestSession && !isICodeSession && !isNoAccountRemoteAccess;
  const headerAccountLabel = getContentString('PPNavAccount') || t('nav.account');
  const headerLogoutLabel = (isGuestSession || isICodeSession)
    ? (getContentString('PPGuestExit') || 'Exit')
    : (getContentString('PortalLogout') || t('nav.logout'));
  const accountInitials = [profile?.firstName?.[0], profile?.lastName?.[0]]
    .filter(Boolean)
    .join('')
    .toUpperCase() || undefined;

  const versionLabel = getContentString('PPPortalVersion') || 'DeepHealth RIS Portal v4.6';
  const footerYear = String(new Date().getFullYear());
  const copyrightRaw =
    getContentString('PPFooterCopyrightPortal')
    || t('portal.copyright')
    || `{year} DeepHealth, Inc. - All rights reserved`;
  const copyrightBody = copyrightRaw
    .replace(/\{\{year\}\}/g, footerYear)
    .replace(/\{year\}/g, footerYear);
  const copyrightLabel = copyrightBody.includes('©') ? copyrightBody : `© ${copyrightBody}`;
  const hipaaLabel = getContentString('PPLoginHipaa') || 'HIPAA Compliant';
  const phiLabel = getContentString('PPLandingPhiProtected') || 'PHI Protected';

  /* ── Sidebar content (shared between permanent and mobile drawer) ── */
  const sidebarContent = (isMobile: boolean) => {
    const effectiveCollapsed = isMobile ? false : visuallyCollapsed;
    const effectiveWidth = isMobile ? 280 : sidebarWidth;

    // Collapsed rail geometry. Expanded, a nav control is a full-width row and its selected
    // state reads as a pill — correct there. Collapsed, that same rule stretched the background
    // across the whole 48px content box, so the selection looked like a clipped pill crammed
    // into the rail. Here each control becomes a fixed square instead: the selection SHAPE is
    // kept (a rounded square) but it now sits inside the narrow area rather than filling it.
    //
    // 44px is deliberate — it clears the 24px minimum target size (WCAG 2.2 SC 2.5.8) with room
    // to spare, and centres exactly in the 64px rail once the List's 8px padding is accounted
    // for (8 + 2 + 44 + 2 + 8 = 64).
    const RAIL_ITEM = 44;
    // Figma Sidebar Nav Item (39340:76513): height 42, pitch 50 → 8px gap; pl 9 / pr 16 / py 8 / radius 8.
    // Keep List as block layout (not flex:1 column) so rows cannot stretch to fill the rail.
    const railItem = effectiveCollapsed
      ? {
          width: RAIL_ITEM,
          height: RAIL_ITEM,
          minWidth: RAIL_ITEM,
          minHeight: RAIL_ITEM,
          maxHeight: RAIL_ITEM,
          mx: 'auto',
          p: 0,
          flexGrow: 0,
          justifyContent: 'center',
          borderRadius: 1,
        }
      : {
          height: 42,
          minHeight: 42,
          maxHeight: 42,
          py: 0,
          px: 0,
          pl: '9px',
          pr: 2,
          gap: '6px',
          flexGrow: 0,
          flexShrink: 0,
          justifyContent: 'flex-start',
          alignItems: 'center',
          borderRadius: 1,
          // Beat theme-pack shorthand padding (e.g. radnet-dls 12×16).
          padding: '8px 16px 8px 9px',
        };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: effectiveWidth, overflow: 'hidden' }}>
        {/* Logo — desktop only; mobile drawer renders its own wordmark in the close-button header */}
        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', py: 1, px: effectiveCollapsed ? 1.75 : 1, flexShrink: 0, justifyContent: effectiveCollapsed ? 'center' : 'flex-start' }}>
            <DHWordmark compact={effectiveCollapsed} />
          </Box>
        )}

        {/* Navigation scroll region — flex:1 lives on the wrapper, NOT the List, so items
            stay Figma-sized (42px + 8px gap) and pack at the top. Empty space below Help is
            intentional (language/collapse stay pinned at the bottom). */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            px: 1,
            pt: isMobile ? 1.5 : 3,
            overflowX: 'hidden',
            overflowY: 'auto',
          }}
        >
          <List
            component="nav"
            aria-label="Primary navigation"
            disablePadding
            sx={{ py: 0 }}
          >
            {tabs.map((tab, index) => {
              const isActive = location.pathname.startsWith(tab.to);
              const label = getContent(tab.contentKey) || t(tab.fallback);
              const isLast = index === tabs.length - 1;
              const navItem = (
                <ListItemButton
                  key={tab.to}
                  component={NavLink}
                  to={tab.to}
                  onClick={isMobile ? () => setMobileOpen(false) : undefined}
                  sx={(theme) => ({
                    ...railItem,
                    mb: isLast ? 0 : 1, // Figma Side Nav gap = 8px
                    bgcolor: isActive ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                    color: isActive ? theme.palette.primary.main : 'text.secondary',
                    '&:hover': {
                      bgcolor: isActive ? alpha(theme.palette.primary.main, 0.08) : 'action.hover',
                      color: isActive ? theme.palette.primary.main : 'text.primary',
                    },
                  })}
                >
                  <ListItemIcon sx={{ minWidth: effectiveCollapsed ? 0 : 24, color: 'inherit', justifyContent: 'center' }}>
                    {tab.badge > 0 ? (
                      <Badge badgeContent={tab.badge} color="info" sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 16, height: 16 } }}>
                        {tab.icon}
                      </Badge>
                    ) : tab.icon}
                  </ListItemIcon>
                  {!effectiveCollapsed && (
                    <ListItemText
                      primary={label}
                      sx={{ my: 0 }}
                      slotProps={{
                        primary: {
                          noWrap: true,
                          sx: {
                            fontSize: 15,
                            fontWeight: 500,
                            lineHeight: '26px',
                            textTransform: 'capitalize',
                          },
                        },
                      }}
                    />
                  )}
                </ListItemButton>
              );

              return effectiveCollapsed ? (
                <Tooltip key={tab.to} title={label} placement="right" arrow>
                  {navItem}
                </Tooltip>
              ) : navItem;
            })}
          </List>
        </Box>

        {/* Bottom: language + restricted-session hooks + collapse */}
        <Box sx={{ py: 1, mt: 1, flexShrink: 0, px: effectiveCollapsed ? 1 : 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Nav account hooks for restricted (iCode / guest) sessions:
              • patient already has a portal account → sign-in form to trade up to a full session
              • patient has no account yet        → "Get Started" create-account hook */}
          {!effectiveCollapsed && (
            (user?.session?.type === 'icode' && user.session.hasAccount) ||
            (user?.session?.type === 'guest' && user.session.hasAccount)
          ) && (
            <NavAccountLogin />
          )}

          {!effectiveCollapsed && (
            (user?.session?.type === 'icode' && !user.session.hasAccount) ||
            (user?.session?.type === 'guest' && user.session.canCreateAccount)
          ) && (
            <NavCreateAccount />
          )}

          {/* Language switcher */}
          {!effectiveCollapsed && <LanguageSwitcher variant="sidebar" />}

          {/* Legal links — mobile drawer only */}
          {isMobile && (
            <>
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ px: 0.5, pb: 0.5, color: 'text.secondary' }}>
                <LegalFooterLinks
                  links={[legalLinks.privacyPolicy, legalLinks.termsOfService, legalLinks.hipaaNotice]}
                  variant="caption"
                  spacing={1}
                />
              </Box>
            </>
          )}

          {/* Collapse toggle (desktop only) — Figma: expand-panel icon + "Collapse Sidebar" */}
          {!isMobile && (
            <Tooltip title={effectiveCollapsed ? (getContent('PPNavExpandSidebar') || t('nav.expandSidebar')) : ''} placement="right">
              <Box
                component="button"
                type="button"
                onClick={toggleCollapsed}
                aria-label={collapsed ? (getContentString('PPNavExpandSidebar') || t('nav.expandSidebar')) : (getContentString('PPNavCollapseSidebar') || t('nav.collapseSidebar'))}
                aria-expanded={!collapsed}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  p: 1,
                  m: 0,
                  border: 0,
                  bgcolor: 'transparent',
                  cursor: 'pointer',
                  color: 'text.secondary',
                  opacity: 0.72,
                  width: '100%',
                  justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'action.hover', opacity: 1 },
                }}
              >
                {effectiveCollapsed ? (
                  <ChevronRightIcon sx={{ fontSize: 22 }} />
                ) : (
                  <>
                    <IconButton component="span" size="medium" tabIndex={-1} sx={{ p: 1, color: 'inherit', pointerEvents: 'none' }}>
                      <ViewSidebarOutlinedIcon sx={{ fontSize: 28 }} />
                    </IconButton>
                    <Typography variant="body2" sx={{ fontSize: 14, letterSpacing: '0.17px', color: 'inherit', whiteSpace: 'nowrap' }}>
                      {getContent('PPNavCollapseSidebar') || t('nav.collapseSidebar')}
                    </Typography>
                  </>
                )}
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', maxHeight: '100vh', overflow: 'hidden', width: '100%', bgcolor: 'background.default' }}>
      {/* Skip nav */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          '&:focus': {
            width: 'auto',
            height: 'auto',
            clip: 'auto',
            overflow: 'visible',
            position: 'fixed',
            top: 8,
            insetInlineStart: 8,
            zIndex: 9999,
            px: 2,
            py: 1,
            bgcolor: 'background.paper',
            color: 'text.primary',
            borderRadius: 2,
            boxShadow: 4,
            fontSize: '0.875rem',
            fontWeight: 500,
            border: 1,
            borderColor: 'divider',
          },
        }}
      >
        {getContent('PortalSkipToMain') || t('nav.skipToMain')}
      </Box>

      {/* Live region */}
      <Box component="div" role="status" aria-live="polite" aria-atomic="true" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
        {routeAnnouncement}
      </Box>

      {/* ══ DESKTOP SIDEBAR ══ */}
      {isDesktop && (
        <Drawer
          variant="permanent"
          aria-label="Sidebar navigation"
          slotProps={{
            paper: {
              onMouseEnter: () => { if (collapsed) setSidebarHovered(true); },
              onMouseLeave: () => { if (sidebarHovered) setSidebarHovered(false); },
            },
          }}
          sx={{
            width: collapsed ? SIDEBAR_COLLAPSED_W : sidebarWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: sidebarWidth,
              boxSizing: 'border-box',
              // Edge border intentionally NOT set here — it lives in theme.config.ts
              // (MuiDrawer.paper.borderInlineEnd, driven by the `divider` token) so it follows
              // the active brand and can be overridden by a style pack or rule. This used to be
              // a hardcoded '1px solid #f0f0f0', which beat the theme and was wrong on any
              // non-light palette.
              transition: sidebarDragWidth ? 'none' : 'width 200ms cubic-bezier(0.4, 0, 0.2, 1)',
              overflowX: 'hidden',
              zIndex: (theme) => theme.zIndex.drawer + (sidebarHovered ? 1 : 0),
              boxShadow: sidebarHovered ? 4 : 'none',
            },
          }}
        >
          {sidebarContent(false)}

          {/* Sidebar drag handle */}
          <Box
            role="slider"
            tabIndex={0}
            aria-label="Resize sidebar"
            aria-valuenow={sidebarWidth}
            aria-valuemin={SIDEBAR_COLLAPSED_W}
            aria-valuemax={400}
            onPointerDown={onSidebarDragStart}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') {
                e.preventDefault();
                setSidebarDragWidth((w) => Math.min((w ?? sidebarWidth) + 8, 400));
              } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                setSidebarDragWidth((w) => Math.max((w ?? sidebarWidth) - 8, SIDEBAR_COLLAPSED_W));
              }
            }}
            sx={{
              position: 'absolute',
              insetInlineEnd: 0,
              top: 0,
              width: 4,
              height: '100%',
              cursor: 'col-resize',
              zIndex: 10,
              '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 },
              '&:hover > div': { bgcolor: 'primary.light' },
              '&:active > div': { bgcolor: 'primary.main' },
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                insetInlineEnd: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 2,
                height: 32,
                borderRadius: 99,
                bgcolor: 'transparent',
                transition: 'background-color 150ms',
              }}
            />
          </Box>
        </Drawer>
      )}

      {/* ══ MOBILE DRAWER ══ */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        aria-label="Navigation menu"
        sx={{
          display: 'block',
          '@media (min-width: 1024px)': { display: 'none' },
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
          },
        }}
      >
        {/* Close button header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, height: 56, borderBottom: 1, borderColor: 'divider' }}>
          <DHWordmark />
          <IconButton onClick={() => setMobileOpen(false)} aria-label={getContentString('PPNavCloseMenu') || t('nav.closeMenu')} size="small">
            <ChevronLeftIcon />
          </IconButton>
        </Box>

        {sidebarContent(true)}
      </Drawer>

      {/* ══ CONTENT AREA ══ */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          width: '100%',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        {/* Top header: mobile [menu][brand] … tools; desktop tools only (welcome/CTA live on page) */}
        <AppBar
          position="sticky"
          color="default"
          elevation={0}
          component="header"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.95),
            color: 'text.primary',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 56, lg: 68 }, gap: 1, px: { xs: 1, sm: 2, lg: 3 } }}>
            {!isDesktop && (
              <IconButton
                onClick={() => setMobileOpen(true)}
                aria-label={getContentString('PPNavOpenMenu') || t('nav.openMenu')}
                aria-expanded={mobileOpen}
                aria-haspopup="dialog"
                color="inherit"
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {!isDesktop && <DHWordmark />}

            <Box sx={{ flexGrow: 1 }} />

            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flexShrink: 0 }}>
              {showHeaderBell && <PortalNotifications />}

              {canAccess('/account') && (
                <IconButton
                  component={Link}
                  to="/account"
                  aria-label={headerAccountLabel}
                  sx={{ p: 0.5, minWidth: 44, minHeight: 44 }}
                >
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      fontSize: 14,
                      fontWeight: 600,
                      bgcolor: accountInitials ? 'primary.main' : 'action.hover',
                      color: accountInitials ? 'primary.contrastText' : 'text.secondary',
                    }}
                  >
                    {accountInitials || <PersonOutlineIcon fontSize="small" />}
                  </Avatar>
                </IconButton>
              )}

              <IconButton
                onClick={handleLogout}
                aria-label={headerLogoutLabel}
                color="inherit"
                sx={{ minWidth: 44, minHeight: 44, color: 'text.secondary' }}
              >
                <LogoutOutlinedIcon />
              </IconButton>
            </Stack>
          </Toolbar>
        </AppBar>

        <AuthorizedAccessBanner />

        {/* Page content */}
        <Box
          component="main"
          id="main-content"
          tabIndex={-1}
          sx={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            px: { xs: 1.5, sm: 2.5 },
            py: { xs: 1.5, sm: 2 },
            pb: { xs: 'calc(80px + env(safe-area-inset-bottom, 0px))' },
            overflowX: 'hidden',
            overflowY: 'auto',
            '@media (min-width: 1024px)': { px: 3, py: 3, pb: 3 },
          }}
        >
          <Outlet />
        </Box>

        <ToastContainer />
        <AriaFab />
        <ParagraphCodeModeBadge />

        {/* Footer (desktop only) — Figma: Powered by | Privacy Terms Help | version • © | HIPAA */}
        {isDesktop && (
          <Box
            component="footer"
            sx={{
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'background.default',
              px: { lg: 3 },
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
              minHeight: 60,
            }}
          >
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontStyle: 'italic', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}
              >
                Powered by
              </Typography>
              <Box
                role="img"
                aria-label="DeepHealth"
                sx={{
                  height: 22,
                  width: 102,
                  bgcolor: 'primary.main',
                  maskImage: `url(${deepHealthLogo})`,
                  maskRepeat: 'no-repeat',
                  maskSize: 'contain',
                  maskPosition: 'left center',
                  WebkitMaskImage: `url(${deepHealthLogo})`,
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskSize: 'contain',
                  WebkitMaskPosition: 'left center',
                }}
              />
            </Stack>

            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
              <LegalFooterLinks
                links={[legalLinks.privacyPolicy, legalLinks.termsOfService]}
                variant="body2"
                spacing={0.5}
                color="primary"
              />
              {helpEnabled && lite.isSectionVisible('help') && canAccess('/help') && (
                <Button
                  component={Link}
                  to="/help"
                  size="small"
                  color="primary"
                  startIcon={<HelpOutlineIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    fontSize: 13,
                    fontWeight: 500,
                    lineHeight: '22px',
                    minWidth: 0,
                    textTransform: 'capitalize',
                    px: '5px',
                    py: 0.5,
                  }}
                >
                  {getContent('PPNavHelp') || t('nav.help')}
                </Button>
              )}
            </Stack>

            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexShrink: 0, ml: 'auto' }}>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', letterSpacing: '0.4px', lineHeight: '24px', whiteSpace: 'nowrap' }}
              >
                {versionLabel}
                {' • '}
                {copyrightLabel}
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  pl: 1,
                  pr: 1.5,
                  py: 0.5,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: '12px',
                  bgcolor: 'transparent',
                }}
              >
                {legalLinks.hipaaNotice?.url ? (
                  <Box
                    component="a"
                    href={legalLinks.hipaaNotice.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'inherit' }}
                  >
                    <VerifiedUserOutlinedIcon sx={{ fontSize: 28, color: 'primary.main' }} aria-hidden />
                    <Stack spacing={0} sx={{ alignItems: 'flex-start' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.3, fontWeight: 500 }}>
                        {hipaaLabel}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.3 }}>
                        {phiLabel}
                      </Typography>
                    </Stack>
                  </Box>
                ) : (
                  <>
                    <VerifiedUserOutlinedIcon sx={{ fontSize: 28, color: 'primary.main' }} aria-hidden />
                    <Stack spacing={0} sx={{ alignItems: 'flex-start' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.3, fontWeight: 500 }}>
                        {hipaaLabel}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.3 }}>
                        {phiLabel}
                      </Typography>
                    </Stack>
                  </>
                )}
              </Box>
            </Stack>
          </Box>
        )}
      </Box>

      {/* ══ MOBILE BOTTOM NAV ══ */}
      {!isDesktop && visibleBottomTabs.length > 0 && (
        <Paper
          component="nav"
          aria-label="Bottom navigation"
          sx={{
            position: 'fixed',
            bottom: 0,
            insetInline: 0,
            zIndex: 40,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.97),
            backdropFilter: 'blur(12px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
          elevation={8}
        >
          <BottomNavigation
            value={bottomNavValue >= 0 ? bottomNavValue : false}
            showLabels
            sx={{ height: 'auto', minHeight: 60, bgcolor: 'transparent', px: 0.5, alignItems: 'stretch' }}
          >
            {visibleBottomTabs.map((tab) => (
              <BottomNavigationAction
                key={tab.to}
                component={NavLink}
                to={tab.to}
                label={getContent(tab.contentKey) || t(tab.fallback)}
                icon={
                  <Box className="bn-icon" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {tab.badge > 0 ? (
                      <Badge
                        badgeContent={tab.badge}
                        max={99}
                        color="info"
                        sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 16, height: 16, px: 0.5, border: 2, borderColor: 'background.paper' } }}
                      >
                        {tab.icon}
                      </Badge>
                    ) : tab.icon}
                  </Box>
                }
                sx={{
                  flex: 1,
                  minWidth: 0,
                  maxWidth: 'none',
                  minHeight: 56,
                  px: 0.25,
                  pt: 0.75,
                  pb: 0.5,
                  gap: 0.25,
                  position: 'relative',
                  color: 'text.secondary',
                  transition: 'color 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                  '& .bn-icon': {
                    width: 48,
                    height: 28,
                    borderRadius: 1,
                    transition: 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                    '& .MuiSvgIcon-root': { fontSize: 22 },
                  },
                  '& .MuiBottomNavigationAction-label': {
                    fontWeight: 400,
                    fontSize: '0.6875rem',
                    lineHeight: 1.2,
                    letterSpacing: 0,
                    textAlign: 'center',
                    maxWidth: '100%',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    overflowWrap: 'anywhere',
                    hyphens: 'auto',
                    transition: 'none',
                  },
                  '&.active, &.Mui-selected': {
                    color: 'primary.main',
                    '& .bn-icon': { bgcolor: (th) => alpha(th.palette.primary.main, 0.12) },
                    '& .MuiBottomNavigationAction-label': { fontWeight: 500, fontSize: '0.6875rem' },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      insetInline: '25%',
                      height: 3,
                      borderRadius: '0 0 4px 4px',
                      bgcolor: 'primary.main',
                    },
                  },
                  '&:not(.active):not(.Mui-selected):hover .bn-icon': { bgcolor: (th) => alpha(th.palette.primary.main, 0.06) },
                  '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
                }}
              />
            ))}
          </BottomNavigation>
        </Paper>
      )}

      {/* Screen session — idle and absolute timeout dialogs */}
      <IdleTimeoutDialog
        open={phase === 'idle-warning'}
        countdown={countdown}
        onExtend={extend}
        onLogout={sessionLogout}
      />
      <AbsoluteTimeoutDialog
        open={phase === 'absolute-warning'}
        countdown={countdown}
        onLogout={sessionLogout}
      />
      <QuickTutorialModal open={tutorialOpen} onClose={() => setTutorialOpen(false)} />
    </Box>
  );
}
